import { US } from '../state/reducers'
import { configureRefreshFetch, fetchJSON } from 'refresh-fetch'
const endpoint = process.env.REACT_APP_DEBUG_MAP_ENDPOINT
const app_build =  process.env.REACT_APP_BUILD
// Dev-against-prod: modern Chrome blocks the cross-site cmv1-* session cookies,
// so local dev authenticates with the server's X-TEZLAB-DEBUG JWT instead.
// REACT_APP_DEBUG_MAP_TOKEN is only ever set in a developer's map/.env; when
// absent (all production builds) no header is sent and nothing changes.
const debug_token = process.env.REACT_APP_DEBUG_MAP_TOKEN
const debugHeaders = debug_token ? { 'X-TEZLAB-DEBUG': debug_token } : {}

const USER_ZOOM = 13
const COUNTRY_ZOOM = 5

// Countries whose geographic centroid is charger-free wilderness get a curated
// dense-population view instead, so the no-location fallback always shows pins.
const COUNTRY_VIEW_OVERRIDES = {
	US: { center: [-98.5795, 38.8238], zoom: 5 }, // geographic center (Kansas corridor)
	CA: { center: [-79.38, 43.65], zoom: 6 },     // Toronto — centroid is Nunavut
	AU: { center: [151.21, -33.87], zoom: 6 },    // Sydney — centroid is outback
	NO: { center: [10.75, 59.91], zoom: 6 },      // Oslo
	SE: { center: [18.07, 59.33], zoom: 6 },      // Stockholm
	FI: { center: [24.94, 60.17], zoom: 6 },      // Helsinki
	NZ: { center: [174.76, -36.85], zoom: 6 },    // Auckland
}

// ================================================= api messages ================================================= \\

export const encryptAES = async (textToEncrypt, encryptionKey) => {
	const encoder = new TextEncoder();
	const textBuffer = encoder.encode(textToEncrypt);
	const keyBuffer = hexStringToByteArray(encryptionKey);

	// Generate a random IV
	const ivBuffer = window.crypto.getRandomValues(new Uint8Array(16));

	const cryptoKey = await window.crypto.subtle.importKey(
			"raw",
			keyBuffer,
			{ name: "AES-CBC", length: 256 },
			false,
			["encrypt", "decrypt"]
	);

	const encrypted = await window.crypto.subtle.encrypt(
			{ name: "AES-CBC", iv: ivBuffer },
			cryptoKey,
			textBuffer
	);

	// Convert encrypted data to a byte array
	const encryptedArray = new Uint8Array(encrypted);

	// Prepend the IV to the encrypted data
	const combinedArray = new Uint8Array(ivBuffer.length + encryptedArray.length);
	combinedArray.set(ivBuffer);
	combinedArray.set(encryptedArray, ivBuffer.length);

	// Convert the combined data (IV + encrypted data) to a base64 encoded string
	const combinedBase64 = btoa(String.fromCharCode(...combinedArray));

	return combinedBase64;
}

export const sha256Digest = async ( textToDigest ) => {
	const encoder = new TextEncoder();
	const digest = await window.crypto.subtle.digest("SHA-256", encoder.encode(`${textToDigest}`));
	const hashArray = Array.from(new Uint8Array(digest));
	const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
	return hashHex;
}

const hexStringToByteArray = (hexString) => {
	if (hexString.length % 2 !== 0) {
		throw "Invalid hexString";
	}
	const byteArray = new Uint8Array(hexString.length / 2);
	for (let i = 0; i < hexString.length; i += 2) {
		byteArray[i / 2] = parseInt(hexString.slice(i, i + 2), 16);
	}
	return byteArray;
}

/**
 * 
 * @returns {Promise<void>}
 */
export const authenticateSession = async () => {
	try {
		const encoder = new TextEncoder();

		//Handhake 1 - Determine IP Address Backend is Seeing
		const response = await fetch(`${endpoint}/hs`,{
			method: "GET",
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
		})
		//Capture IP Address from Response Headers
		const client_ip = response.headers.get('x-conn-ip');

		//Calculate the current Front End Key
		const s = await window.crypto.subtle.digest("SHA-256", encoder.encode(`${client_ip}`));
		const i = client_ip ? client_ip.length * 10 : 1000;
		const pbf = encoder.encode(app_build.toString());
		const iK = await window.crypto.subtle.importKey(
			"raw",
			pbf,
			{name: "PBKDF2"},
			false,
			["deriveBits", "deriveKey"]
		);

		const dB = await window.crypto.subtle.deriveBits(
			{
				name: "PBKDF2",
				salt: s,
				iterations: i,
				hash: "SHA-256"
			},
			iK,
			256
		);

		//Express the Front End Key as a Hex value
		const feKeyString = Array.from(new Uint8Array(dB)).map(byte => byte.toString(16).padStart(2, '0')).join('');

		//Generate a random encryption key for the handhake (handshake_nonce)
		const encKey = await window.crypto.subtle.generateKey(
			{
				name: "AES-CBC",
				length: 256
			},
			true,
			["encrypt", "decrypt"]
		);

		const ekb = await window.crypto.subtle.exportKey("raw", encKey);
		const handshake_nonce = Array.from( new Uint8Array(ekb) ).map(byte => ('0' + byte.toString(16)).slice(-2)).join('');

		//Generate the payload that the handhake expects, including the handshake nonce, and encrypt it with the front end key
		const handshake_data = await encryptAES(JSON.stringify({handshake_nonce, client_ip}), feKeyString);
		const handshake_param = await sha256Digest(`${client_ip}handshake${app_build}`)

		//Shake the backend's hand
		const hs_response = await fetch(`${endpoint}/hs`,{
			method: "POST",
			body: JSON.stringify({
				[handshake_param] : handshake_data,
			}),
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
			credentials: "include"
		});
		
		//Backend should have responded with a new current secret which we can use to init a session
		//Calculate a session secret using the current secret and the handshake nonce we used a moment ago
		const hs_response_data = await hs_response.json();
		const session_secret = await encryptAES(JSON.stringify({current_secret: hs_response_data.cs, client_ip}), handshake_nonce)
		const session_secret_param = await sha256Digest(`${client_ip}start_session${handshake_nonce}`)

		//Send the session secret to start a new session
		const session_response = await fetch(`${endpoint}/si`,{
			method: "POST",
			body: JSON.stringify({
				[session_secret_param] : session_secret,
			}),
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
			credentials: "include"
		});

		//The response should set a cmv1-s cookie which we will use to authenticate all future requests, subsequent requests will update the cookie
		
	} catch (error){
		return { error: typeof error === 'string' ? error : 'Something went wrong.' }
	}
}

const fetchyFetch = async (url, options = {}) => {
	const { response } = await fetchJSON(url, options)
	return response
}

const shouldRefreshToken = error => {
	console.warn('shouldRefreshToken', { error, bool: error.status === 401 })
	return error.status === 401
}

const myFetch = configureRefreshFetch({
	fetch: fetchyFetch,
	shouldRefreshToken,
	refreshToken: authenticateSession
})

// ================================================= error messages ================================================= \\

export const CHARGER_FETCH_ERROR =
	'Something went wrong when retrieving data for this charger.'
export const POSTS_FETCH_ERROR =
	'Something went wrong fetching the posts for this charger.'
export const ZAPS_FETCH_ERROR =
	'Something went wrong when retrieving Zaps fro this charger.'
export const RECENT_FETCH_ERROR =
	'Something went wrong when retrieving recent charges at this location.'
export const PINS_FETCH_ERROR =
	'Something went wrong when retrieving chargers for this region.'
export const PINS_ABORT_ERROR = 'AbortError'

// ================================================= storage related ================================================= \\

/**
 * 
 * @param {{longitude:number,latitude:number,zoom:number}} savedView
 * @returns 
 */
export const setStoredMapView = savedView => {
	if (typeof savedView !== 'object') return
	console.log('saving:', { savedView })
	window.localStorage.setItem('tezlab-saved-view', JSON.stringify(savedView))
}

/**
 * 
 * @returns {{longitude:number,latitude:number,zoom:number}|null}
 */
export const retrieveStoredMapView = () => {
	const saved = window.localStorage.getItem('tezlab-saved-view')
	return saved ? JSON.parse(saved) : saved
}

/**
 * 
 * @param {boolean} bool 
 * @returns {void}
 */
export const setStoredFilterOption = bool => {
	if (typeof bool !== 'boolean') return
	window.localStorage.setItem('tezlab-show-non-tes-only', bool)
}

/**
 * 
 * @returns {boolean}
 */
export const retrieveStoredFilterOption = () => {
	const str = window.localStorage.getItem('tezlab-show-non-tes-only')
	return str === 'true' ? true : false
}

/**
 * 
 * @param {boolean} bool 
 * @returns {void}
 */
export const setStoredFilterButtonToggleOption = bool => {
	if (typeof bool !== 'boolean') return
	window.localStorage.setItem('tezlab-show-expanded-filter', bool)
}

/**
 * 
 * @returns {boolean}
 */
export const retrieveStoredButtonToggleOption = () => {
	const str = window.localStorage.getItem('tezlab-show-expanded-filter')
	return str === 'false' ? false : true
}

// ================================================= location related ================================================= \\

/**
 * @returns {Promise<{latitude:number,longitude:number,zoom:number,error?:string}>}
 */
export const locateUser = async () => {
	return new Promise((resolve, reject) => {
		if (!navigator.geolocation) return reject({ error: 'no geolocation' })
		navigator.geolocation.getCurrentPosition(
			({ coords: { latitude, longitude } }) =>
				resolve({ latitude, longitude, zoom: USER_ZOOM }),
			e => reject({ error: e?.message })
		)
	})
}

/**
 *
 * @returns
 */
export const locateUserCountry = async () => {
	const body = {
		center_lat: 0,
		center_lon: 0,
		corner_1_lat: 0.01,
		corner_1_lon: 0.01,
		corner_2_lat: -0.01,
		corner_2_lon: -0.01,
	}
	try {
		const response = await myFetch(endpoint, {
			method: 'POST',
			body: JSON.stringify(body),
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
			credentials: "include"
		})
		// this doesn't work in development because of CORS
		const countryCode = response.headers.get('x-tezlab-country') ?? 'US'
		if (COUNTRY_VIEW_OVERRIDES[countryCode]) return COUNTRY_VIEW_OVERRIDES[countryCode]
		// this is a arcgis service that returns centroid data for ISO country codes
		const responseTwo = await myFetch(
			`https://services9.arcgis.com/l9yXFvhjz46ekkZV/arcgis/rest/services/Countries_Centroids/FeatureServer/0/query?where=ISO+%3D+%27${countryCode}%27&outFields=*&f=pgeojson`
		)
		const json = await responseTwo.json()
		if (
			!Array.isArray(json?.features) ||
			typeof json.features[0].properties?.longitude !== 'number' ||
			typeof json.features[0].properties?.latitude !== 'number'
		)
			throw new Error('Invalid response from arcgis')
		const center = [
			json.features[0].properties.longitude,
			json.features[0].properties.latitude,
		]
		return { center, zoom: COUNTRY_ZOOM }
	} catch (error) {
		return US
	}
}

// ================================================= map related ================================================= \\

export const getPins = (
	centerLat,
	centerLon,
	cornerOneLat,
	cornerOneLon,
	cornerTwoLat,
	cornerTwoLon
) => {
	const body = {
		center_lat: centerLat,
		center_lon: centerLon,
		corner_1_lat: cornerOneLat,
		corner_1_lon: cornerOneLon,
		corner_2_lat: cornerTwoLat,
		corner_2_lon: cornerTwoLon,
	}

	const controller = new AbortController()
	const { signal } = controller
	const promise = async () => {
		try {
			const response = await myFetch(endpoint, {
				method: 'POST',
				body: JSON.stringify(body),
				headers: {
					'Content-Type': 'application/json',
				...debugHeaders,
					'x-tezlab-ua': `TezLabMapWeb/${app_build}`
				},
				signal,
				credentials: "include"
			})
			const json = await response.json()
			if (json.message) throw json.message
			const data = json?.pins ?? []
			return { data, error: '' }
		} catch (error) {
			// if this is an abort we need to keep the isFetchingPins boolean positive
			return {
				error:
					error?.name === PINS_ABORT_ERROR
						? PINS_ABORT_ERROR
						: PINS_FETCH_ERROR,
			}
		}
	}
	const abort = () => controller.abort()

	return {
		promise,
		abort,
	}
}

// ================================================= charger related ================================================= \\

export const getChargerData = async id => {
	try {
		const response = await myFetch(`${endpoint}/chargers/${id}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
			credentials: "include"
		})
		const countryCode = response.headers.get('x-tezlab-country') ?? 'US' // America FUCK YEAH!
		const currencyCode = response.headers.get('x-tezlab-local-currency') ?? 'USD' // America FUCK YEAH!
		const json = await response.json()
		console.log('api getChargerData', { json, countryCode, currencyCode })
		if (!json) throw new Error('no data')
		if (json.message) throw json.message
		const { awards, congestion, ...data } = json
		return { data, awards, popular_times: congestion, error: '', countryCode, currencyCode }
	} catch (error) {
		return { error: typeof error === 'string' ? error : CHARGER_FETCH_ERROR, countryCode: 'US', currencyCode: 'USD' }
	}
}

export const getPostsData = async detail => {
	try {
		const response = await myFetch(`${endpoint}${detail}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
			credentials: "include"
		})
		const json = await response.json()
		if (!json) throw new Error('no data')
		if (json.message) throw json.message
		return { data: json.posts, error: '' }
	} catch (error) {
		return { error: typeof error === 'string' ? error : POSTS_FETCH_ERROR }
	}
}

export const getZapsData = async detail => {
	try {
		const response = await myFetch(`${endpoint}${detail}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
			credentials: "include"
		})
		const json = await response.json()
		if (!json) throw new Error('no data')
		if (json.message) throw json.message
		return { data: json.zaps, error: '' }
	} catch (error) {
		return { error: typeof error === 'string' ? error : ZAPS_FETCH_ERROR }
	}
}

export const getRecentChargesData = async detail => {
	try {
		const response = await myFetch(`${endpoint}${detail}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...debugHeaders,
				'x-tezlab-ua': `TezLabMapWeb/${app_build}`
			},
			credentials: "include"
		})
		const json = await response.json()
		if (!json) throw new Error('no data')
		if (json.message) throw json.message
		return { data: json.recent_charges, error: '' }
	} catch (error) {
		return { error: typeof error === 'string' ? error : RECENT_FETCH_ERROR }
	}
}

// ================================================= auth related ================================================= \\

